import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Seo, breadcrumbJsonLd, localBusinessJsonLd } from '@/components/Seo';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { LeadForm } from '@/components/LeadForm';
import { LOCATIONS, SITE, SERVICES } from '@/data/site-config';
import { locationContent } from '@/data/locations-content';
import { ArrowRight, MapPin, Phone } from 'lucide-react';

export default function LocationPage() {
  const { slug } = useParams();
  const meta = LOCATIONS.find((l) => l.slug === slug);
  const content = locationContent[slug];
  if (!meta || !content) return <Navigate to="/locations" replace />;
  const path = `/locations/${slug}`;
  const breadcrumbs = [{ name: 'Home', path: '/' }, { name: 'Locations', path: '/locations' }, { name: `${meta.name}, FL`, path }];

  return (
    <>
      <Seo title={`Personal Trainer Serving ${meta.name}, FL`} description={content.metaDescription} canonical={path} jsonLd={[breadcrumbJsonLd(breadcrumbs), localBusinessJsonLd()]} />
      <Breadcrumbs items={breadcrumbs} />
      <section className="container-x py-10">
        <span className="eyebrow"><MapPin size={12} /> {meta.name}, {SITE.stateAbbr}</span>
        <h1 className="font-display text-4xl md:text-5xl font-bold mt-3">{content.h1}</h1>
        <p className="text-[color:var(--ft-muted)] text-lg mt-4 max-w-3xl">{content.intro}</p>
      </section>

      <section className="container-x grid gap-8 lg:grid-cols-12 items-start">
        <div className="lg:col-span-7 prose-dark">
          <h2>How we work with {meta.name} clients</h2>
          <p>{content.community}</p>

          <h2>Why {meta.name} clients pick a private studio over a chain gym</h2>
          <ul>{content.whyDrive.map((it, i) => (<li key={i}>{it}</li>))}</ul>

          <h2>Coaching options for {meta.name} residents</h2>
          <p>Most {meta.name} clients choose one of the following based on their goal and schedule:</p>
          <ul>
            {SERVICES.map((s) => (
              <li key={s.slug}><Link className="text-[color:var(--ft-text)] hover:text-[color:var(--ft-accent)] underline" to={`/services/${s.slug}`}>{s.title}</Link> — {s.short}</li>
            ))}
          </ul>

          <h2>Where we&rsquo;re located</h2>
          <p>Fitness Training 360 does <strong>not</strong> operate a separate location in {meta.name}. Our private studio is at <strong className="text-[color:var(--ft-text)]">{SITE.address.full}</strong> — about a {meta.drive} drive from {meta.name}. All sessions take place at the Stuart studio by appointment.</p>

          <h2>{meta.name} FAQs</h2>
          {content.faqs.map((f, i) => (
            <details key={i} className="surface p-5 not-prose mt-2"><summary className="font-semibold cursor-pointer">{f.q}</summary><p className="mt-3 text-[color:var(--ft-muted)] leading-relaxed">{f.a}</p></details>
          ))}
        </div>
        <aside className="lg:col-span-5 lg:sticky lg:top-24 space-y-4">
          <LeadForm source={path} testIdPrefix={`loc-${slug}`} />
          <div className="surface p-5">
            <h3 className="font-display font-semibold">Prefer to call?</h3>
            <p className="text-[color:var(--ft-muted)] text-sm mt-1">We&rsquo;re available by phone or text during business hours.</p>
            <a href={`tel:${SITE.phoneE164}`} className="btn btn-secondary mt-3 w-full"><Phone size={16} /> {SITE.phone}</a>
          </div>
        </aside>
      </section>

      <section className="container-x py-10">
        <h2 className="font-display text-2xl font-bold mb-4">Other Martin County communities we serve</h2>
        <div className="flex flex-wrap gap-2">
          {LOCATIONS.filter((l) => l.slug !== slug).map((l) => (
            <Link key={l.slug} to={`/locations/${l.slug}`} className="tag hover:border-[color:var(--ft-accent)] hover:text-[color:var(--ft-accent)]">{l.name} <ArrowRight size={12} className="inline ml-1" /></Link>
          ))}
        </div>
      </section>
    </>
  );
}
